import React, { useState, useEffect } from 'react';
import { useAuth, SESSION_ENDED_KEY } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import BrandLogo from './BrandLogo';
import { normalizeWebsiteUrl } from '../utils/website-url';

type Mode = 'login' | 'signup' | 'forgot' | 'reset';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius)',
  fontSize: '1rem',
  boxSizing: 'border-box'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.5rem',
  color: 'var(--ink-soft)',
  fontWeight: '500'
};

// Requirements belong beside the field BEFORE it is filled in. Both of this
// form's rules — a parseable site and eight characters — used to be
// discoverable only by breaking them, one after the other.
const hintStyle: React.CSSProperties = {
  marginTop: '0.35rem',
  color: 'var(--muted)',
  fontSize: '0.8rem'
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--brand)',
  cursor: 'pointer',
  fontSize: '0.875rem',
  textDecoration: 'underline'
};

// All per-mode copy and test ids in one place — adding a mode means one entry here
const MODE_COPY: Record<Mode, {
  heading: string;
  subheading: string;
  submitIdle: string;
  submitBusy: string;
  formTestId: string;
  submitTestId: string;
  errorFallback: string;
}> = {
  login: {
    heading: 'Welcome back',
    subheading: 'Manage your product feeds, image matches, and performance.',
    submitIdle: 'Sign in',
    submitBusy: 'Signing in…',
    formTestId: 'login-form',
    submitTestId: 'login-button',
    errorFallback: 'Login failed'
  },
  signup: {
    heading: 'Create your publisher account',
    subheading: 'Verify your site next, then connect the affiliate product feeds you choose.',
    submitIdle: 'Create publisher account',
    submitBusy: 'Creating account…',
    formTestId: 'signup-form',
    submitTestId: 'signup-button',
    errorFallback: 'Signup failed'
  },
  forgot: {
    heading: 'Reset your password',
    subheading: "Enter your account email and we'll send a reset link if it matches an account.",
    submitIdle: 'Send reset link',
    submitBusy: 'Sending...',
    formTestId: 'forgot-password-form',
    submitTestId: 'forgot-password-button',
    errorFallback: 'Request failed'
  },
  reset: {
    heading: 'Choose a new password',
    subheading: 'Use at least eight characters for your ImgAff account.',
    submitIdle: 'Set new password',
    submitBusy: 'Saving...',
    formTestId: 'reset-password-form',
    submitTestId: 'reset-password-button',
    errorFallback: 'Password reset failed'
  },
};

function initialMode(): Mode {
  return window.location.pathname === '/reset-password' ? 'reset' : 'login';
}

function resetTokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('token') || '';
}

const Login: React.FC = () => {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  // A spent reset token cannot be spent again, so leaving the form on screen
  // offers an action whose only possible outcome is an error.
  const [resetDone, setResetDone] = useState(false);
  const { login, signup } = useAuth();

  // A 401 mid-task clears the session and drops the publisher here. Say so:
  // the card is headed "Welcome back", which on its own reads as though they
  // chose to come. Read-and-clear, so a later voluntary visit stays silent.
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_ENDED_KEY)) {
      sessionStorage.removeItem(SESSION_ENDED_KEY);
      setInfo('Your session ended. Sign in to pick up where you left off.');
    }
  }, []);

  const showSignup = mode === 'signup';

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setInfo('');
    setPassword('');
    setConfirmPassword('');
    if (next === 'login' && window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      if (mode === 'forgot') {
        if (!email) {
          setError('Email is required');
          return;
        }
        const result = await apiService.forgotPassword(email);
        setInfo(result.message || 'If an account exists for that email, a reset link has been sent.');
        return;
      }

      if (mode === 'reset') {
        const token = resetTokenFromUrl();
        if (!token) {
          setError('This reset link is invalid. Please request a new one.');
          return;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          return;
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        const result = await apiService.resetPassword(token, password);
        setInfo(result.message || 'Password updated. You can now log in with your new password.');
        // The token is spent. Take it out of the address bar so it stops
        // travelling in history, bookmarks and any copied link.
        window.history.replaceState(null, '', '/reset-password');
        setResetDone(true);
        return;
      }

      // login / signup validation
      if (!email) {
        setError('Email is required');
        return;
      }
      if (!password) {
        setError('Password is required');
        return;
      }
      if (showSignup) {
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          return;
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        if (!website) {
          setError('Website URL is required');
          return;
        }
        const normalized = normalizeWebsiteUrl(website);
        if (!normalized) {
          setError("That doesn't look like a website address. Try something like glossyu.com");
          return;
        }
        await signup(email, password, normalized);
      } else {
        await login(email, password);
      }
    } catch (err: any) {
      setError(err.message || MODE_COPY[mode].errorFallback);
    } finally {
      setLoading(false);
    }
  };

  const copy = MODE_COPY[mode];
  const submitLabel = loading ? copy.submitBusy : copy.submitIdle;

  const showEmail = mode !== 'reset' && !resetDone;
  const showPassword = mode !== 'forgot' && !resetDone;

  return (
    <div className="auth-shell">
      <div className="auth-intro">
        <a href="https://imgaff.com" aria-label="ImgAff website"><BrandLogo /></a>
        <p className="auth-eyebrow">Shoppable images for affiliate publishers</p>
        <h1>Turn relevant images into useful product choices.</h1>
        <p>ImgAff matches blog images with products from affiliate feeds you control—and stays quiet when relevance is weak.</p>
      </div>
      <div data-testid={copy.formTestId} className="auth-card">
        <div className="auth-card-heading">
          <h2>{copy.heading}</h2>
          <p>{copy.subheading}</p>
        </div>

        <form onSubmit={handleSubmit}>
          {showEmail && (
            <div className="auth-field">
              <label htmlFor="email" style={labelStyle}>
                Email Address
              </label>
              <input
                id="email"
                data-testid="email-input"
                type="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
                placeholder="Enter your email"
              />
            </div>
          )}

          {showPassword && (
            <div className="auth-field">
              <label htmlFor="password" style={labelStyle}>
                {mode === 'reset' ? 'New Password' : 'Password'}
              </label>
              <input
                id="password"
                data-testid="password-input"
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
                placeholder={mode === 'reset' ? 'Enter your new password' : 'Enter your password'}
              />
              {(showSignup || mode === 'reset') && (
                <div style={hintStyle}>At least 8 characters.</div>
              )}
            </div>
          )}

          {(mode === 'reset' || mode === 'signup') && !resetDone && (
            <div className="auth-field">
              <label htmlFor="confirm-password" style={labelStyle}>
                {mode === 'reset' ? 'Confirm New Password' : 'Confirm Password'}
              </label>
              <input
                id="confirm-password"
                data-testid="confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
                placeholder={mode === 'reset' ? 'Repeat your new password' : 'Repeat your password'}
              />
            </div>
          )}

          {showSignup && (
            <div className="auth-field">
              <label htmlFor="website" style={labelStyle}>
                Website URL
              </label>
              {/* `text`, not `url`: see normalizeWebsiteUrl. The browser's own
                  url validation rejects a bare domain before handleSubmit ever
                  runs, so the inline error below could never be reached. */}
              <input
                id="website"
                data-testid="website-input"
                type="text"
                inputMode="url"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={inputStyle}
                placeholder="glossyu.com"
              />
              <div style={hintStyle}>The site you publish on. We'll add https:// if you leave it off.</div>
            </div>
          )}

          {error && (
            <div data-testid="error-message" className="error" style={{
              backgroundColor: 'var(--danger-bg)',
              color: 'var(--danger)',
              padding: '0.75rem',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
              border: '1px solid rgba(180,35,24,0.24)'
            }}>
              {error}
            </div>
          )}

          {info && (
            <div data-testid="info-message" style={{
              backgroundColor: 'var(--good-bg)',
              color: 'var(--good)',
              padding: '0.75rem',
              borderRadius: 'var(--radius)',
              marginBottom: '1rem',
              border: '1px solid rgba(22,132,91,0.24)'
            }}>
              {info}
            </div>
          )}

          {!resetDone && (
            <button
              data-testid={copy.submitTestId}
              type="submit"
              disabled={loading}
              className="auth-submit"
            >
              {submitLabel}
            </button>
          )}
        </form>

        <div className="auth-links">
          {mode === 'login' && (
            <button
              data-testid="forgot-password-link"
              onClick={() => switchMode('forgot')}
              style={linkButtonStyle}
            >
              Forgot password?
            </button>
          )}
          {(mode === 'login' || mode === 'signup') && (
            <button
              data-testid="toggle-signup"
              onClick={() => switchMode(showSignup ? 'login' : 'signup')}
              style={linkButtonStyle}
            >
              {showSignup ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
            </button>
          )}
          {(mode === 'forgot' || mode === 'reset') && (
            <button
              data-testid="back-to-login"
              onClick={() => switchMode('login')}
              style={linkButtonStyle}
            >
              Back to log in
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
